import { Button, Col, Form, Input, Row, Select, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  CRITERIA_FIELD_OPTIONS,
  OPERATOR_OPTIONS,
  LIST_OPERATORS,
  type TemplateContextFormValues,
} from './templateContext.constants';

/**
 * Dynamic criteria rows used inside both Add and Edit modals.
 * Must be rendered inside a <Form> that has a `criteria` Form.List field.
 */
const CriteriaFields = () => {
  return (
    <Form.Item
      label="Matching Criteria"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      style={{ marginBottom: 8 }}
    >
      <Form.List name="criteria">
        {(fields, { add, remove }) => (
          <>
            {fields.length > 0 && (
              <Row gutter={8} style={{ marginBottom: 4 }}>
                <Col span={7}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Field
                  </Typography.Text>
                </Col>
                <Col span={7}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Operator
                  </Typography.Text>
                </Col>
                <Col span={8}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Value
                  </Typography.Text>
                </Col>
              </Row>
            )}
            {fields.map(({ key, name, ...restField }) => (
              <Row key={key} gutter={8} align="middle">
                <Col span={7}>
                  <Form.Item
                    {...restField}
                    name={[name, 'field']}
                    rules={[{ required: true, message: 'Required' }]}
                    style={{ marginBottom: 8 }}
                  >
                    <Select
                      showSearch
                      placeholder="Field"
                      options={CRITERIA_FIELD_OPTIONS}
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item
                    {...restField}
                    name={[name, 'operator']}
                    rules={[{ required: true, message: 'Required' }]}
                    style={{ marginBottom: 8 }}
                  >
                    <Select placeholder="Operator" options={OPERATOR_OPTIONS} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <CriteriaValueField name={name} />
                </Col>
                <Col span={2} style={{ textAlign: 'center' }}>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(name)}
                    style={{ marginBottom: 8 }}
                  />
                </Col>
              </Row>
            ))}
            <Button
              type="dashed"
              onClick={() =>
                add({
                  field: '',
                  operator: '$eq_simple',
                  value: '',
                  values: [],
                })
              }
              icon={<PlusOutlined />}
              block
            >
              Add Condition
            </Button>
          </>
        )}
      </Form.List>
    </Form.Item>
  );
};

/** Switches between text input and tags input based on operator */
const CriteriaValueField = ({ name }: { name: number }) => {
  const form = Form.useFormInstance<TemplateContextFormValues>();
  const operator = Form.useWatch(['criteria', name, 'operator'], form);
  const isList = LIST_OPERATORS.has(operator);

  if (isList) {
    return (
      <Form.Item
        name={[name, 'values']}
        rules={[{ required: true, message: 'At least one value required' }]}
        style={{ marginBottom: 8 }}
      >
        <Select
          mode="tags"
          placeholder="Type value + Enter"
          tokenSeparators={[',']}
          style={{ width: '100%' }}
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item
      name={[name, 'value']}
      rules={[{ required: true, message: 'Value required' }]}
      style={{ marginBottom: 8 }}
    >
      <Input placeholder="Value" />
    </Form.Item>
  );
};

export default CriteriaFields;
